import { useUserActiveOrganization } from "@/api";
import { TypographyXS } from "@/components/typography/XS";
import Dialog from "@/components/ui/dialog";
import React, { useCallback, useEffect, useState } from "react";
import { generateSlugFromName } from "../../../hooks/generateSlugFromName";
import CreateAppDetail from "../CreateAppDetail";
import {
  AdditionalManifestErrors,
  AdditionalManifestFields
} from "./AdditionalManifestFields";
import { useLocalProjectFolder } from "./hooks/localProjectFolder";
import { CustomSubmit } from "./types/customSubmit.type";
import { isLibraryKey } from "./types/library.type";
import {
  Manifest,
  PythonManifest,
  isPythonManifest
} from "./types/manifest.type";
import { MissingInputsError } from "./utils/error";

interface Props {
  setSubmit: React.Dispatch<React.SetStateAction<CustomSubmit | null>>;
}

export const LocalAppProject = ({ setSubmit }: Props) => {
  const [appSlug, setAppSlug] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [appSlugError, setAppSlugError] = useState<string | null>(null);
  const [folderError, setFolderError] = useState<string | null>(null);
  const [additionalManifestErrors, setAdditionalManifestErrors] =
    useState<AdditionalManifestErrors>({
      appFileError: null,
      requirementsError: null,
      libraryError: null,
      portError: null,
      pythonVersionError: null,
      coverImageError: null
    });

  const { activeOrganization } = useUserActiveOrganization();
  const {
    isSupported,
    manifest,
    selectFolder,
    folderPath,
    missingManifestFields,
    saveLocalFiles,
    deploy
  } = useLocalProjectFolder();

  const setSlugOnChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setAppSlug(generateSlugFromName(e.target.value));
  };

  const handleFolderSelect = async () => {
    try {
      await selectFolder();
      setFolderError(null);
    } catch (error) {
      console.error(error);
      if (error instanceof Error) setFolderError(error.message);
      else
        setFolderError(
          "Unexpected error. Could not open folder, please select another or report the issue."
        );
    }
  };

  const handleAdditionalManifestFields = useCallback(
    (partial: Partial<Manifest>, data: FormData): Partial<Manifest> => {
      let m = partial;
      m.cover_image = getField(data, "cover-image") || manifest?.cover_image;
      const port = getField(data, "port") || manifest?.port;
      m.port = port ? Number(port) : undefined;
      const p = manifest && "python" in manifest && manifest.python;
      m = {
        ...m,
        python: {
          library: getField(data, "library") || (p && p?.library),
          app_file: getField(data, "python-app") || (p && p?.app_file),
          requirements_file:
            getField(data, "python-requirements") ||
            (p && p?.requirements_file),
          version: getField(data, "python-version") || (p && p?.version)
        }
      } as Partial<PythonManifest>;

      const newManifestErrors = errorsByManifest(m);
      setAdditionalManifestErrors(newManifestErrors);

      if (Object.values(newManifestErrors).some((err) => err !== null))
        throw MissingInputsError;

      return m;
    },
    [manifest]
  );

  const onSubmit = useCallback(
    async (form: HTMLFormElement) => {
      if (!activeOrganization?.slug) throw new Error("No organization");
      const data = new FormData(form);
      let m: Partial<Manifest> = {
        deploy: {
          app: getField(data, "appSlug"),
          organization: activeOrganization.slug
        },
        name: getField(data, "appName"),
        description: getField(data, "appDescription")
      };

      let hasError = false;
      if (!m.name) {
        setNameError("Name cannot be empty");
        hasError = true;
      } else setNameError(null);

      if (!m.deploy?.app) {
        setAppSlugError("Slug cannot be empty");
        hasError = true;
      } else setAppSlugError(null);
      if (missingManifestFields) {
        m = handleAdditionalManifestFields(m, data);
      }

      if (hasError) throw MissingInputsError;
      await saveLocalFiles(m);

      const res = await deploy({
        displayName: m.name || "",
        description: m.description || "",
        appSlug: m.deploy?.app || "",
        organizationSlug: m.deploy?.organization || ""
      });

      return res;
    },
    [
      missingManifestFields,
      handleAdditionalManifestFields,
      activeOrganization?.slug,
      saveLocalFiles,
      deploy
    ]
  );

  useEffect(() => {
    if (manifest?.deploy?.app) setAppSlug(manifest.deploy.app);
    else if (manifest?.name) setAppSlug(generateSlugFromName(manifest.name));
  }, [manifest?.name, manifest?.deploy?.app]);

  useEffect(() => {
    if (!activeOrganization?.slug || !folderPath) return () => setSubmit(null);
    setSubmit(() => onSubmit);

    return () => setSubmit(null);
  }, [activeOrganization?.slug, setSubmit, folderPath, onSubmit]);

  if (!isSupported)
    return (
      <div className="mt-4 grid grid-cols-[20%_1fr] items-center gap-4">
        <Dialog.Description className="ml-2 col-start-2 text-sm">
          Your browser does not support this feature.
        </Dialog.Description>
      </div>
    );

  return (
    <div className="mt-4 grid items-center gap-2 mb-2">
      <CreateAppDetail
        id="folder"
        label="Folder"
        type="button"
        value={folderPath || "Click to select a local project folder"}
        onClick={handleFolderSelect}
        className="hover:cursor-pointer"
        error={folderError}
      />
      {manifest && (
        <>
          <CreateAppDetail
            id="appName"
            name="appName"
            label="App name"
            placeholder="Name"
            defaultValue={manifest.name}
            onChange={setSlugOnChange}
            required
            error={nameError}
          />
          <CreateAppDetail
            id="appDescription"
            name="appDescription"
            label="App description"
            placeholder="Description"
            defaultValue={manifest.description}
          />
          <CreateAppDetail
            id="appSlug"
            name="appSlug"
            label="App slug"
            placeholder="slug-from-name"
            value={appSlug}
            error={appSlugError}
            onChange={setSlugOnChange}
            required
          >
            <TypographyXS className="col-start-2 ml-2 text-brand-gray-400 dark:text-brand-gray-300 font-light tracking-wider italic">
              &quot;a-z&quot; &quot;-&quot; &quot;0-9&quot;
            </TypographyXS>
          </CreateAppDetail>
          {missingManifestFields && isPythonManifest(manifest) && (
            <AdditionalManifestFields
              manifest={manifest}
              {...additionalManifestErrors}
            />
          )}
        </>
      )}
    </div>
  );
};

function getField(f: FormData, key: string): string {
  return f.get(key)?.toString()?.trim() || "";
}

function libraryError(libraryKey: unknown): string | null {
  if (!libraryKey) return "You must select a library before deploying";
  if (!isLibraryKey(libraryKey)) return "Invalid input";
  return null;
}

function portError(port: number | undefined): string | null {
  if (port === undefined) return "Port cannot be empty";
  if (port % 1 !== 0) return "Port cannot contain decimals";
  if (port < 0) return "Port must be a positive integer";
  return null;
}

function errorsByManifest(
  m: Partial<PythonManifest>
): AdditionalManifestErrors {
  return {
    coverImageError: m.cover_image ? null : "Cover image cannot be empty",
    libraryError: libraryError(m.python?.library),
    portError: portError(m.port),
    appFileError: m.python?.app_file ? null : "Python app path cannot be empty",
    pythonVersionError: m.python?.version
      ? null
      : "Python version cannot be empty",
    requirementsError: m.python?.requirements_file
      ? null
      : "Requirements path cannot be empty"
  };
}
